/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useState } from 'react';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { setStoreManualOverride } from '../../settings/infra/storeProfileRepo';
import { getCachedStoreProfile } from '../../settings/application/storefrontStatus';
import { invalidateHomeSnapshot } from '../../shell/home/homeSnapshotRepo';
import { isStoreOpenNow, type StoreProfile } from '../../settings/domain/storeProfileTypes';
import { showSuccess } from '../../shared/ui/appToast';
import { formatLocalDateTime } from '@shared/vueltoDateTime';

function isOverrideActive(profile: StoreProfile, mode: 'force_close' | 'force_open'): boolean {
  if (profile.manualOverride !== mode) return false;
  if (!profile.manualOverrideUntil) return true;
  return new Date(profile.manualOverrideUntil) > new Date();
}

function untilLabel(iso: string | null): string {
  if (!iso) return 'que vuelvas al horario';
  return formatLocalDateTime(iso);
}

/** Estado del canal online + pausa rápida (manual_override force_close). */
export function HomeBusyBanner() {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const publicStoreOn = usePublicStoreFeaturesVisible();
  const hasAppointments = useHasBusinessCapability('appointments');
  const hasDelivery = useHasBusinessCapability('delivery_fulfillment');
  const bookingOnly = hasAppointments && !hasDelivery;
  const [profile, setProfile] = useState<StoreProfile | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!branchId || !publicStoreOn) {
      setProfile(null);
      return;
    }
    try {
      setProfile(await getCachedStoreProfile(branchId));
    } catch {
      setProfile(null);
    }
  }, [branchId, publicStoreOn]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const apply = useCallback(
    async (mode: 'auto' | 'force_close' | 'force_open', minutes?: number) => {
      if (!branchId) return;
      setLoading(true);
      try {
        const next = await setStoreManualOverride(branchId, mode, minutes);
        setProfile(next);
        // La sección storefront del home_snapshot también lleva el override.
        invalidateHomeSnapshot(branchId);
        showSuccess(
          mode === 'force_close'
            ? 'Cerrado'
            : mode === 'force_open'
              ? 'Abierto'
              : 'Volviste al horario',
          mode === 'force_close'
            ? `Hasta ${untilLabel(next.manualOverrideUntil)} no te pueden ${bookingOnly ? 'reservar' : 'pedir'}.`
            : mode === 'force_open'
              ? `Te pueden ${bookingOnly ? 'reservar' : 'pedir'} online hasta que vuelvas al horario.`
              : 'Recibís según el horario.'
        );
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'onboarding', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo cambiar.' });
      } finally {
        setLoading(false);
      }
    },
    [branchId, bookingOnly]
  );

  if (!publicStoreOn || !branchId || !profile?.isPublic) return null;

  const paused = isOverrideActive(profile, 'force_close');
  const forcedOpen = isOverrideActive(profile, 'force_open');
  const openByHours = isStoreOpenNow(profile.businessHours);
  const accepting = forcedOpen || (!paused && openByHours);

  const title = paused ? 'Cerrado' : accepting ? 'Abierto' : 'Cerrado';
  const badgeLabel = paused ? 'Pausada' : forcedOpen ? 'Abierta por vos' : accepting ? 'En horario' : 'Fuera de horario';
  const badgeTone: 'open' | 'closed' | 'muted' = paused ? 'closed' : accepting ? 'open' : 'muted';

  let sub: string;
  if (paused) {
    sub = profile.manualOverrideUntil
      ? `La cerraste vos. Hasta ${untilLabel(profile.manualOverrideUntil)}. No pueden ${
          bookingOnly ? 'reservar' : 'pedir'
        } online.`
      : `La cerraste vos. No pueden ${bookingOnly ? 'reservar' : 'pedir'} online.`;
  } else if (forcedOpen) {
    sub = 'La abriste vos. Recibís aunque no toque horario.';
  } else if (accepting) {
    sub = bookingOnly
      ? 'Estás recibiendo reservas. Pausá un rato sin tocar el horario.'
      : 'Estás recibiendo pedidos. Pausá un rato sin tocar el horario.';
  } else {
    sub = bookingOnly
      ? 'Ahora no te pueden reservar online. Si atendés, abrí igual.'
      : 'Ahora no te pueden pedir online. Si atendés, abrí igual.';
  }

  return (
    <View
      style={[
        styles.card,
        badgeTone === 'open' && styles.cardOpen,
        badgeTone === 'closed' && styles.cardPaused,
      ]}
    >
      <View style={styles.textCol}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>{title}</Text>
          <View
            style={[
              styles.badge,
              badgeTone === 'open' && styles.badgeOpen,
              badgeTone === 'closed' && styles.badgeClosed,
              badgeTone === 'muted' && styles.badgeMuted,
            ]}
          >
            <View
              style={[
                styles.dot,
                badgeTone === 'open' && styles.dotOpen,
                badgeTone === 'closed' && styles.dotClosed,
              ]}
            />
            <Text
              style={[
                styles.badgeText,
                badgeTone === 'open' && styles.badgeTextOpen,
                badgeTone === 'closed' && styles.badgeTextClosed,
              ]}
            >
              {badgeLabel}
            </Text>
          </View>
        </View>
        <Text style={styles.sub}>{sub}</Text>
      </View>
      {loading ? (
        <ActivityIndicator color={ticketColors.gold} />
      ) : paused || forcedOpen ? (
        <TouchableOpacity
          style={styles.primaryBtn}
          onPress={() => void apply('auto')}
          accessibilityRole="button"
          accessibilityLabel="Volver al horario"
        >
          <Text style={styles.primaryBtnText}>Volver al horario</Text>
        </TouchableOpacity>
      ) : accepting ? (
        <View style={styles.actions}>
          <TouchableOpacity
            style={styles.chip}
            onPress={() => void apply('force_close', 20)}
            accessibilityRole="button"
            accessibilityLabel="Pausar 20 minutos"
          >
            <Text style={styles.chipText}>20 minutos</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.chip}
            onPress={() => void apply('force_close', 60)}
            accessibilityRole="button"
            accessibilityLabel="Pausar 1 hora"
          >
            <Text style={styles.chipText}>1 hora</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.chip}
            onPress={() => void apply('force_close', 180)}
            accessibilityRole="button"
            accessibilityLabel="Pausar 3 horas"
          >
            <Text style={styles.chipText}>3 horas</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity
          style={styles.primaryBtn}
          onPress={() => void apply('force_open')}
          accessibilityRole="button"
          accessibilityLabel="Abrir ahora"
        >
          <Text style={styles.primaryBtnText}>Abrir ahora</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  cardOpen: {
    borderColor: 'rgba(21,128,61,0.35)',
    backgroundColor: 'rgba(21,128,61,0.06)',
  },
  cardPaused: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  textCol: { flex: 1, minWidth: 0, gap: 4 },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  title: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
  },
  badgeOpen: { backgroundColor: 'rgba(21,128,61,0.12)' },
  badgeClosed: { backgroundColor: 'rgba(185,28,28,0.1)' },
  badgeMuted: { backgroundColor: ticketColors.paper2 },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
  },
  badgeTextOpen: { color: ticketColors.ok },
  badgeTextClosed: { color: ticketColors.bad },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: ticketColors.ink2,
  },
  dotOpen: { backgroundColor: ticketColors.ok },
  dotClosed: { backgroundColor: ticketColors.bad },
  sub: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 16,
  },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, maxWidth: 220, justifyContent: 'flex-end' },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  chipText: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  primaryBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: ticketColors.gold,
    flexShrink: 0,
  },
  primaryBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
});
